import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import PptxGenJS from 'pptxgenjs';
import {
  GeneratedScenario,
  GenerateScenariosRequest,
  GenerateScenariosResponse,
  GenerateModuleSummariesRequest,
  GenerateModuleSummariesResponse,
  GeneratedModuleSummary,
  GenerateExerciseDocumentRequest,
  GenerateExerciseDocumentResponse,
  GenerateExerciseDocumentResponseMarkdown,
  RedTeamCritiqueRequest,
  RedTeamCritiqueResponse,
  ApplyCritiqueRequest,
  ApplyCritiqueResponse,
  PlanPptSlidesRequest,
  PlanPptSlidesResponse,
  GeneratePptContentRequest,
  GeneratePptContentResponse,
  GeneratePptSlidesRequest,
  GeneratedPptContentSlide,
  PptSlideContent,
  GenerateCrisisImagesRequest,
  GenerateCrisisImagesResponse,
} from '../models/wizard.models';
import config from '../../../../config';

@Injectable({
  providedIn: 'root',
})
export class ScenarioService {
  private apiUrl = config.API_GATEWAY_ENDPOINT;

  constructor(private readonly http: HttpClient) {}

  /**
   * Genera escenarios llamando a la API real
   * @param payload Datos del wizard para generar escenarios
   * @returns Promise con la respuesta de la API
   */
  async generateScenariosFromAPI(
    payload: GenerateScenariosRequest,
  ): Promise<GeneratedScenario[]> {
    console.log(payload);
    try {
      const response = await firstValueFrom(
        this.http.post<GenerateScenariosResponse>(
          `${this.apiUrl}/api/ai/generate-scenarios`,
          payload,
        ),
      );

      if (Array.isArray(response)) {
        return response;
      }

      return response.scenarios ?? [];
    } catch (error) {
      console.error('Error generating scenarios from API:', error);
      throw error;
    }
  }

  /**
   * Genera resúmenes de módulos llamando a la API real
   * @param payload Datos del wizard para generar módulos
   * @returns Promise con la respuesta de la API
   */
  async generateModuleSummariesFromAPI(
    payload: GenerateModuleSummariesRequest,
  ): Promise<GeneratedModuleSummary[]> {
    console.log(payload);
    try {
      const response = await firstValueFrom(
        this.http.post<GenerateModuleSummariesResponse>(
          `${this.apiUrl}/api/ai/generate-module-summaries`,
          payload,
        ),
      );

      if (Array.isArray(response)) {
        return response;
      }

      return response.moduleReviews ?? [];
    } catch (error) {
      console.error('Error generating module summaries from API:', error);
      throw error;
    }
  }

  /**
   * Genera el documento de ejercicio completo llamando a la API real
   * @param payload Datos completos del wizard para generar el documento
   * @returns Promise con la respuesta de la API
   */
  async generateExerciseDocumentFromAPI(
    payload: GenerateExerciseDocumentRequest,
  ): Promise<GenerateExerciseDocumentResponseMarkdown> {
    try {
      const response = await firstValueFrom(
        this.http.post<GenerateExerciseDocumentResponse>(
          `${this.apiUrl}/api/ai/generate-exercise-document`,
          payload,
        ),
      );

      if ('document' in response && response.document) {
        return {
          type: 'complete',
          markdown: this.stripMarkdownCodeFence(response.document),
        };
      }

      if ('markdown' in response && response.markdown) {
        return {
          ...response,
          markdown: this.stripMarkdownCodeFence(response.markdown),
        };
      }

      return {
        type: 'complete',
        markdown: '',
      };
    } catch (error) {
      console.error('Error generating exercise document from API:', error);
      throw error;
    }
  }

  private stripMarkdownCodeFence(markdown: string): string {
    const trimmed = markdown.trim();
    const fenceMatch = trimmed.match(/^```markdown\n([\s\S]*?)\n```$/i);

    if (fenceMatch?.[1]) {
      return fenceMatch[1].trim();
    }

    return trimmed;
  }

  /**
   * Solicita crítica del Red Team sobre el documento de ejercicio
   * @param payload Datos del documento y contexto para la crítica
   * @returns Promise con la respuesta de la API
   */
  async requestRedTeamCritiqueFromAPI(
    payload: RedTeamCritiqueRequest,
  ): Promise<RedTeamCritiqueResponse> {
    console.log(payload);
    try {
      const response = await firstValueFrom(
        this.http.post<RedTeamCritiqueResponse>(
          `${this.apiUrl}/api/ai/red-team-critique`,
          payload,
        ),
      );
      return response;
    } catch (error) {
      console.error('Error requesting red team critique from API:', error);
      throw error;
    }
  }

  /**
   * Aplica la crítica del Red Team al documento y lo refina
   * @param payload Documento original y crítica para aplicar mejoras
   * @returns Promise con la respuesta de la API
   */
  async applyCritiqueFromAPI(
    payload: ApplyCritiqueRequest,
  ): Promise<ApplyCritiqueResponse> {
    console.log(payload);
    try {
      const response = await firstValueFrom(
        this.http.post<ApplyCritiqueResponse>(
          `${this.apiUrl}/api/ai/apply-critique`,
          payload,
        ),
      );
      return response;
    } catch (error) {
      console.error('Error applying critique from API:', error);
      throw error;
    }
  }

  /**
   * Genera plan de diapositivas PowerPoint basado en el documento
   * @param payload Documento y contexto para generar el plan de slides
   * @returns Promise con la respuesta de la API
   */
  async planPptSlidesFromAPI(
    payload: PlanPptSlidesRequest,
  ): Promise<PlanPptSlidesResponse> {
    console.log(payload);
    try {
      const response = await firstValueFrom(
        this.http.post<PlanPptSlidesResponse>(
          `${this.apiUrl}/api/ai/plan-ppt-slides`,
          payload,
        ),
      );
      return response;
    } catch (error) {
      console.error('Error planning PPT slides from API:', error);
      throw error;
    }
  }

  /**
   * Genera contenido de diapositivas basado en el plan
   * @param payload Documento, plan y contexto para generar contenido de slides
   * @returns Promise con arreglo de slides enriquecidas
   */
  async generatePptContentFromAPI(
    payload: GeneratePptContentRequest,
  ): Promise<GeneratePptContentResponse> {
    console.log(payload);
    try {
      const response = await firstValueFrom(
        this.http.post<GeneratePptContentResponse>(
          `${this.apiUrl}/api/ai/generate-ppt-content`,
          payload,
        ),
      );
      return response;
    } catch (error) {
      console.error('Error generating PPT content from API:', error);
      throw error;
    }
  }

  /**
   * Renderiza y descarga archivo PowerPoint final
   * @param payload Contenido de slides y contexto
   * @returns Promise con blob y nombre sugerido de archivo
   */
  async generatePptSlidesFromAPI(
    payload: GeneratePptSlidesRequest,
  ): Promise<{ blob: Blob; filename: string }> {
    console.log(payload);

    const fallbackFilename = this.buildPptFilename(payload.context);

    try {
      const response = await firstValueFrom(
        this.http.post<
          | GeneratePptContentResponse
          | { slides?: GeneratePptContentResponse; filename?: string }
        >(`${this.apiUrl}/api/ai/generate-ppt-slides`, payload),
      );

      const slides = this.extractSlidesFromResponse(response, payload.slides);
      const responseFilename =
        typeof response === 'object' && !Array.isArray(response)
          ? response.filename
          : undefined;

      return this.renderPptWithPptxGenJs(
        slides,
        responseFilename || fallbackFilename,
      );
    } catch (error) {
      // Si la API de render falla, usamos el contenido local ya generado para no bloquear la descarga.
      console.warn(
        'Error generating PPT slides from API, using local slides fallback:',
        error,
      );
      return this.renderPptWithPptxGenJs(payload.slides, fallbackFilename);
    }
  }

  private extractSlidesFromResponse(
    response:
      | GeneratePptContentResponse
      | { slides?: GeneratePptContentResponse; filename?: string },
    fallbackSlides: GeneratedPptContentSlide[],
  ): GeneratedPptContentSlide[] {
    if (Array.isArray(response)) {
      return response;
    }

    if (Array.isArray(response?.slides)) {
      return response.slides;
    }

    return fallbackSlides;
  }

  private async renderPptWithPptxGenJs(
    slides: GeneratedPptContentSlide[],
    filename: string,
  ): Promise<{ blob: Blob; filename: string }> {
    const presentation = new PptxGenJS();
    presentation.layout = 'LAYOUT_WIDE';
    presentation.author = 'Crisis Exercise Tool';
    presentation.subject = 'Crisis exercise presentation';
    presentation.title = 'Crisis exercise presentation';

    slides
      .sort((left, right) => left.slideNumber - right.slideNumber)
      .forEach((slideData) => {
        const slide = presentation.addSlide();
        const normalizedLayout = (slideData.layout || 'title_and_bullets')
          .toLowerCase()
          .trim();

        slide.addText(slideData.title || `Slide ${slideData.slideNumber}`, {
          x: 0.5,
          y: 0.35,
          w: 12.3,
          h: 0.6,
          fontSize: 24,
          bold: true,
          color: '1F2937',
          fontFace: 'Calibri',
        });

        const bodyLines = this.getBodyLinesFromContent(
          slideData.content,
          normalizedLayout,
        );

        slide.addText(bodyLines.join('\n'), {
          x: 0.8,
          y: 1.2,
          w: 11.5,
          h: 5.4,
          fontSize: 16,
          color: '111827',
          valign: 'top',
          breakLine: true,
          fontFace: 'Calibri',
        });

        if (slideData.speakerNotes) {
          slide.addText(`Speaker Notes: ${slideData.speakerNotes}`, {
            x: 0.8,
            y: 6.8,
            w: 11.5,
            h: 0.45,
            fontSize: 10,
            color: '6B7280',
            italic: true,
            fontFace: 'Calibri',
          });
        }
      });

    if (slides.length === 0) {
      const slide = presentation.addSlide();
      slide.addText('No slide content was provided.', {
        x: 1.0,
        y: 3.0,
        w: 10.0,
        h: 0.8,
        fontSize: 22,
        bold: true,
        color: '374151',
        align: 'center',
      });
    }

    const blob = (await presentation.write({ outputType: 'blob' })) as Blob;
    return {
      blob,
      filename,
    };
  }

  private getBodyLinesFromContent(
    content: PptSlideContent,
    layout: string,
  ): string[] {
    const asRecord = (content ?? {}) as Record<string, unknown>;
    const bullets = this.readStringArray(asRecord['bullets']);
    const leftColumn = this.readStringArray(asRecord['leftColumn']);
    const rightColumn = this.readStringArray(asRecord['rightColumn']);
    const timelineItems = Array.isArray(asRecord['timelineItems'])
      ? (asRecord['timelineItems'] as Array<Record<string, unknown>>)
      : [];
    const headline =
      typeof asRecord['headline'] === 'string'
        ? asRecord['headline'].trim()
        : '';
    const moduleNumber =
      typeof asRecord['moduleNumber'] === 'string' ||
      typeof asRecord['moduleNumber'] === 'number'
        ? String(asRecord['moduleNumber'])
        : '';
    const timescale =
      typeof asRecord['timescale'] === 'string'
        ? asRecord['timescale'].trim()
        : '';
    const summary =
      typeof asRecord['summary'] === 'string' ? asRecord['summary'].trim() : '';

    if (layout === 'title' && headline) {
      return [headline];
    }

    if (layout === 'module_intro') {
      const lines: string[] = [];
      if (moduleNumber) {
        lines.push(`Module: ${moduleNumber}`);
      }
      if (timescale) {
        lines.push(`Timescale: ${timescale}`);
      }
      if (summary) {
        lines.push(summary);
      }
      if (lines.length > 0) {
        return lines;
      }
    }

    if (layout === 'timeline' && timelineItems.length > 0) {
      const lines = timelineItems
        .map((item) => {
          const time =
            typeof item['time'] === 'string'
              ? item['time'].trim()
              : 'Timeline item';
          const event =
            typeof item['event'] === 'string' ? item['event'].trim() : '';
          return event ? `- ${time}: ${event}` : `- ${time}`;
        })
        .filter((line) => line.trim().length > 0);

      if (lines.length > 0) {
        return lines;
      }
    }

    if (layout === 'two_column') {
      const lines: string[] = [];
      if (leftColumn.length > 0) {
        lines.push('Left Column');
        lines.push(...leftColumn.map((item) => `- ${item}`));
      }
      if (rightColumn.length > 0) {
        if (lines.length > 0) {
          lines.push('');
        }
        lines.push('Right Column');
        lines.push(...rightColumn.map((item) => `- ${item}`));
      }
      if (lines.length > 0) {
        return lines;
      }
    }

    if (bullets.length > 0) {
      return bullets.map((item) => `- ${item}`);
    }

    if (headline) {
      return [headline];
    }

    return this.flattenContentObject(asRecord);
  }

  private flattenContentObject(content: Record<string, unknown>): string[] {
    const entries = Object.entries(content)
      .map(([key, value]) => {
        if (value === null || value === undefined) {
          return '';
        }

        if (typeof value === 'string') {
          return `${key}: ${value}`;
        }

        if (typeof value === 'number' || typeof value === 'boolean') {
          return `${key}: ${String(value)}`;
        }

        if (Array.isArray(value)) {
          const serializedArray = value
            .map((item) => {
              if (typeof item === 'string') {
                return item;
              }

              try {
                return JSON.stringify(item);
              } catch {
                return String(item);
              }
            })
            .join(', ');
          return `${key}: ${serializedArray}`;
        }

        try {
          return `${key}: ${JSON.stringify(value)}`;
        } catch {
          return `${key}: ${String(value)}`;
        }
      })
      .filter((line) => line.trim().length > 0);

    return entries.length > 0 ? entries : ['No content provided.'];
  }

  private readStringArray(value: unknown): string[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return value
      .map((item) => (typeof item === 'string' ? item.trim() : ''))
      .filter((item) => item.length > 0);
  }

  private buildPptFilename(
    context: GeneratePptSlidesRequest['context'],
  ): string {
    const scenarioTitle = context.selectedScenario?.title || 'crisis-exercise';
    const safeTitle = scenarioTitle
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 50);

    return `${safeTitle || 'crisis-exercise'}-presentation.pptx`;
  }

  /**
   * Genera imágenes de crisis basadas en el escenario y módulos
   * @param payload Escenario y módulos para generar imágenes
   * @returns Promise con la respuesta de la API
   */
  async generateCrisisImagesFromAPI(
    payload: GenerateCrisisImagesRequest,
  ): Promise<GenerateCrisisImagesResponse> {
    console.log(payload);
    try {
      const response = await firstValueFrom(
        this.http.post<GenerateCrisisImagesResponse>(
          `${this.apiUrl}/api/ai/generate-crisis-images`,
          payload,
        ),
      );
      return response;
    } catch (error) {
      console.error('Error generating crisis images from API:', error);
      throw error;
    }
  }
}
